<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="1000px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    {{ actionName }}
                </h1>
            </v-card-title>

            <v-card-text>
                <v-container class="pa-0">
                    <v-row>
                        <v-col cols="12" md="4" lg="4">
                            <v-autocomplete
                                v-model="userTask.responsibleUserId"
                                :items="users"
                                label="Wykonawca"
                                :filter="customUsersFilter"
                                :loading="usersSearchLoading"
                                :search-input.sync="usersSearch"
                                :error-messages="responsibleUserIdErrors"
                                class="v-text-field--placeholder-dark"
                                outlined
                                :disabled="isUserTaskFinished || action === 'openUserTask'"
                                dense
                                item-value="id"
                                no-data-text="Nie znaleziono"
                                clearable
                            >
                                <template slot="no-data">
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!usersSearch">
                                                Wpisz treść aby odnaleźć <strong>wykonawcę</strong>.
                                            </template>
                                            <template v-else-if="usersSearchLoading">
                                                Trwa wyszukiwanie...
                                            </template>
                                            <template v-else>
                                                Nie znaleziono
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                                <template slot="selection" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ data.item.username }}
                                    </template>
                                </template>
                                <template slot="item" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ data.item.username }}
                                    </template>
                                </template>
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4" lg="5">
                            <v-menu
                                v-model="menuPlannedFinishDate"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template #activator="{on}">
                                    <v-text-field
                                        v-model="userTask.plannedFinishDate"
                                        label="Planowana data ukończenia"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        clearable
                                        :disabled="isUserTaskFinished || action === 'openUserTask'"
                                        outlined
                                        dense
                                        :error-messages="plannedFinishDateErrors"
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="userTask.plannedFinishDate"
                                    first-day-of-week="1"
                                    @input="menuPlannedFinishDate = false"
                                />
                            </v-menu>
                        </v-col>
                        <v-col v-if="!isNewUserTask" cols="12" md="4" lg="3">
                            <v-text-field
                                label="Data ukończenia"
                                outlined
                                dense
                                :value="formatDate(userTask.finishDate).replace(/\//g, '-')"
                                disabled
                                required
                            />
                        </v-col>
                        <v-col cols="12" md="8" lg="8">
                            <v-text-field
                                v-model="userTask.title"
                                label="Temat"
                                outlined
                                dense
                                :disabled="isUserTaskFinished || action === 'openUserTask'"
                                :error-messages="titleErrors"
                                required
                            />
                        </v-col>
                        <v-col v-if="!isNewUserTask" cols="12" md="4" lg="4">
                            <v-select
                                v-model="userTask.userTaskStatusId"
                                label="Status"
                                :items="userTaskStatuses"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono statusu"
                                outlined
                                :disabled="isUserTaskFinished || action === 'openUserTask'"
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-textarea
                                v-model="userTask.description"
                                label="Opis"
                                :disabled="isUserTaskFinished || action === 'openUserTask'"
                                :error-messages="descriptionErrors"
                                outlined
                            />
                        </v-col>
                        <v-col v-if="action === 'openUserTask'" cols="12" md="4" lg="4">
                            <v-text-field
                                label="Data utworzenia"
                                outlined
                                :value="formatDate(userTask.createdAt).replace(/\//g, '-')"
                                dense
                                disabled
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    :disabled="actionLoading"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="action !== 'openUserTask'"
                    color="success"
                    text
                    :disabled="isUserTaskFinished"
                    :loading="actionLoading"
                    @click="saveUserTask"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import customFilters from '@/mixins/customFilters'
import dateFormatter from '@/mixins/dateFormatter'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { EventBus } from '@/utils/eventBus'

import { isEmpty } from 'lodash'
import { required } from 'vuelidate/lib/validators'
import { format } from 'date-fns'

let searchTimer

export default {
    name: 'UserTaskDialog',
    mixins: [ customFilters, dateFormatter ],
    props: {
        isOpen: {
            type: Boolean
        },
        userTaskData: {
            type: Object,
            default: null
        },
        actionName: {
            type: String,
            default: null
        },
        action: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            userTask: {
                title: null,
                description: null,
                responsibleUserId: null,
                plannedFinishDate: null,
            },
            menuFinishDate: false,
            menuPlannedFinishDate: false,
            usersSearch: null,
            usersSearchLoading: false,
            users: [],
            actionLoading: false,
        }
    },
    computed: {
        isUserTaskFinished() {
            return this.userTaskData.userTaskStatusId === 20
        },
        isNewUserTask() {
            return isEmpty(this.userTaskData)
        },
        userTaskStatuses() {
            return this.$store.state.userTasks.userTasks.userTaskStatuses
        },
        responsibleUserIdErrors() {
            const errors = []

            if (!this.$v.userTask.responsibleUserId.$dirty) return errors
            if (!this.$v.userTask.responsibleUserId.required)
                errors.push('Wykonawca jest wymagany.')

            return errors
        },
        plannedFinishDateErrors() {
            const errors = []

            if (!this.$v.userTask.plannedFinishDate.$dirty) return errors
            if (!this.$v.userTask.plannedFinishDate.required)
                errors.push('Planowana data ukończenia jest wymaga.')

            return errors
        },
        titleErrors() {
            const errors = []

            if (!this.$v.userTask.title.$dirty) return errors
            if (!this.$v.userTask.title.required)
                errors.push('Temat jest wymagany.')

            return errors
        },
        descriptionErrors() {
            const errors = []

            if (!this.$v.userTask.description.$dirty) return errors
            if (!this.$v.userTask.description.required)
                errors.push('Opis jest wymagany.')

            return errors
        },
    },
    validations: {
        userTask: {
            responsibleUserId: {
                required
            },
            plannedFinishDate: {
                required
            },
            title: {
                required
            },
            description: {
                required
            }
        }
    },
    watch: {
        isOpen() {
            if ( this.action === 'openUserTask' || this.action === 'updateUserTask') {
                this.userTask = JSON.parse(JSON.stringify(this.userTaskData))
                this.users.push(this.userTask.responsibleUser)
                this.userTask.plannedFinishDate = this.userTask.plannedFinishDate.split('T')[0]
            }
        },
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        }
    },
    methods: {
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        saveUserTask() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading = true
            let payload = JSON.parse(JSON.stringify(this.userTask))

            payload.plannedFinishDate +=`T${format(new Date(), 'HH:mm:ss')}Z`
            this.$store.dispatch(`userTasks/${this.action}`, payload)
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    EventBus.$emit('getUserTasks')
                    this.$emit('get-user-tasks')
                    this.actionLoading = false
                    this.onClose()
                })
        },
        onClose() {
            setTimeout(() => {
                this.$v.$reset()
                this.userTask = {}
            }, 500)
            this.$emit('on-close')
        },
    }
}
</script>